import { useEffect, useRef, useState } from 'react';
import { useRecipeEventTypeId } from '../eventTypes';
import {
  fetchRecipeList,
  RECIPE_SEARCH_DEBOUNCE_MS,
  RECIPE_SEARCH_MIN,
} from './recipeCatalog';

/**
 * Debounced recipe search for modals/pickers (Add food, ingredient search).
 * Waits for the user to stop typing before calling the API.
 */
export function useRecipeCatalog(query, { enabled = true, excludeId = null } = {}) {
  const eventTypeId = useRecipeEventTypeId();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const requestIdRef = useRef(0);

  const trimmed = (query || '').trim();
  const hasMinQuery = trimmed.length >= RECIPE_SEARCH_MIN;

  // Debounce: only update the query used for fetching after the user pauses typing
  useEffect(() => {
    if (!enabled || !hasMinQuery) {
      setDebouncedQuery('');
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(trimmed), RECIPE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [enabled, trimmed, hasMinQuery]);

  const shouldFetch = enabled && eventTypeId && debouncedQuery.length >= RECIPE_SEARCH_MIN;

  useEffect(() => {
    if (!shouldFetch) {
      if (!enabled || !hasMinQuery) {
        setRecipes([]);
        setTotalCount(0);
      }
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    (async () => {
      setLoading(true);
      const res = await fetchRecipeList(eventTypeId, { search: debouncedQuery });
      if (cancelled || requestId !== requestIdRef.current) return;

      if (res.success) {
        let rows = res.recipes;
        if (excludeId) {
          rows = rows.filter((r) => String(r.id) !== String(excludeId));
        }
        setRecipes(rows);
        setTotalCount(res.totalCount);
      } else {
        setRecipes([]);
        setTotalCount(0);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldFetch, eventTypeId, debouncedQuery, excludeId, enabled, hasMinQuery]);

  const isDebouncing = enabled && hasMinQuery && trimmed !== debouncedQuery;

  return {
    recipes,
    loading: loading || isDebouncing,
    totalCount,
    eventTypeId,
    isSearch: debouncedQuery.length >= RECIPE_SEARCH_MIN,
  };
}
